/**
 * The app's accent: one colour the player chooses in Settings, drawn by the
 * shell's accent engine (`packages/shell/INTEGRATION.md` 4.5 and 4.6,
 * `docs/decisions/0056`, `0057`) so that it reads on whichever base, Light or
 * Dark, the app shows.
 *
 * - **What is stored** is the colour the player chose, as a hex: a preset's
 *   `hex` or a custom colour (the settings blob's `accent`, synced with an
 *   account like every other setting). Never the engine's output: that is
 *   worked out again for the base on show, every time.
 * - **Interlock's signature** is {@link INTERLOCK_SIGNATURE}, its blue: the
 *   default accent and the picker's first swatch, named "Interlock" (the
 *   owner's decision of 2026-10-04, INTEGRATION.md 4.6). `app.css` carries
 *   the engine's answer for it on both bases, so a page with nothing stored
 *   paints blue from its first frame; the client's `lint` gates it
 *   (`house-check.mjs --accent`) and `test/theme/accent-contrast.test.ts`
 *   holds that block to the engine.
 * - **Resolved** against {@link ACCENT_PRESETS_TO_RESOLVE}, the shell's
 *   `presetsToResolve(signature)`: the signature resolves as a preset (never
 *   "adjusted"), and so does Harbour, the shared preset that gave way to it.
 *
 * This half is pure and imports only the engine (`@artibens/app-shell/accent`,
 * no Svelte), so `state/settings-io.ts` can validate a stored accent with it.
 * `show-accent.ts` is the half that touches the page and the boot cache.
 *
 * The board look is not the accent and never reads it (`docs/decisions/0044`,
 * `0056`): it paints the game canvas in fixed colours of its own, and this
 * paints the app around it.
 */
import {
  normaliseHex,
  presetsToResolve,
  resolveAccent,
  type AccentBase,
  type AccentPreset,
  type AccentTokens,
} from '@artibens/app-shell/accent';

/**
 * Interlock's signature colour (INTEGRATION.md 4.6, the owner's decision of
 * 2026-10-04): the default accent, and the first swatch of the picker, named
 * after the game. On Light the engine keeps it exactly; on Dark it lightens
 * it until every pair the shell gates reads (`app.css`).
 */
export const INTERLOCK_SIGNATURE = { name: 'Interlock', hex: '#2d5fd6' } as const;

/** The default accent: the signature's colour, and what anything unreadable falls back to. */
export const INTERLOCK_ACCENT: string = INTERLOCK_SIGNATURE.hex;

/**
 * What a stored accent is looked up in: the picker's row (the signature,
 * then the shared presets that look unlike it) and then the presets that
 * gave way to the signature, so each resolves to its own tuned colours.
 */
export const ACCENT_PRESETS_TO_RESOLVE: readonly AccentPreset[] =
  presetsToResolve(INTERLOCK_SIGNATURE);

/**
 * The accent a stored value names: a hex colour, normalised to `#rrggbb` in
 * lower case. Anything else (nothing, a number, a word, a broken hex) is
 * Interlock's blue.
 */
export function parseAccent(value: unknown): string {
  return normaliseHex(value) ?? INTERLOCK_ACCENT;
}

/** The seven identity tokens for `hex` on `base`, from the shell's engine. */
export function accentTokens(hex: string, base: AccentBase): AccentTokens {
  return resolveAccent(parseAccent(hex), base, { presets: ACCENT_PRESETS_TO_RESOLVE }).tokens;
}
